import { useState } from 'react';
import { FiSend, FiCheckCircle } from 'react-icons/fi';
import { submitDemoRequest } from '../../services/api';
import { StateCitySelect } from '../../components/common';
import styles from './LandingLayout.module.css';

const emptyForm = {
  name: '', email: '', phone: '', derasarName: '',
  addressLine1: '', addressLine2: '', city: '', state: '', pincode: '',
  message: '',
};

const RequestDemoPage = () => {
  const [form, setForm]               = useState(emptyForm);
  const [errors, setErrors]           = useState({});
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const e = {};
    if (!form.name.trim())         e.name         = 'Your name is required';
    if (!form.email.trim())        e.email        = 'Your email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email address';
    if (!form.phone.trim())        e.phone        = 'Phone number is required';
    if (!form.derasarName.trim())  e.derasarName  = 'Derasar / temple name is required';
    if (!form.addressLine1.trim()) e.addressLine1 = 'Address is required';
    if (!form.state.trim())        e.state        = 'State is required';
    if (!form.city.trim())         e.city         = 'City is required';
    if (!form.pincode.trim())      e.pincode      = 'Pincode is required';
    return e;
  };

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(er => ({ ...er, [e.target.name]: '' }));
  };

  const handleStateChange = (val) => {
    setForm(f => ({ ...f, state: val }));
    if (errors.state) setErrors(er => ({ ...er, state: '' }));
  };

  const handleCityChange = (val) => {
    setForm(f => ({ ...f, city: val }));
    if (errors.city) setErrors(er => ({ ...er, city: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setSubmitError('');
    try {
      await submitDemoRequest(form);
      setSubmitted(true);
      setForm(emptyForm);
    } catch (err) {
      const msg = err?.response?.data?.message || '';
      if (msg.includes('already exists')) {
        setSubmitError('A demo request from this email is already pending. We will be in touch shortly.');
      } else {
        setSubmitError('Something went wrong. Please try again or email us at jainamsevasetu@gmail.com');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionInner}>
        <div className={styles.sectionLabel}>Try Before You Subscribe</div>
        <h2 className={styles.sectionTitle}>Request a Demo</h2>
        <p className={styles.sectionLead}>
          Want to see Jainam Seva Setu in action for your Derasar? Fill in the form below and our
          team will set up a <strong>14-day free trial</strong> so you can
          explore all features before committing to a subscription.
        </p>

        {submitted ? (
          <div className={styles.successBox}>
            <h3><FiCheckCircle style={{ marginRight: 8, verticalAlign: 'middle' }} />Request Received!</h3>
            <p>
              Thank you for your interest. Our team will review your request and get back to you
              within one business day with trial access details.
            </p>
          </div>
        ) : (
          <form className={styles.supportForm} onSubmit={handleSubmit} noValidate>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="name">Your Name *</label>
                <input id="name" name="name" className={styles.formInput} value={form.name} onChange={handleChange} placeholder="Raj Mehta" autoComplete="name" />
                {errors.name && <span className={styles.formError}>{errors.name}</span>}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="email">Email Address *</label>
                <input id="email" name="email" type="email" className={styles.formInput} value={form.email} onChange={handleChange} placeholder="raj@example.com" autoComplete="email" />
                {errors.email && <span className={styles.formError}>{errors.email}</span>}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="phone">Phone Number *</label>
                <input id="phone" name="phone" type="tel" className={styles.formInput} value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" autoComplete="tel" />
                {errors.phone && <span className={styles.formError}>{errors.phone}</span>}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="derasarName">Derasar Name *</label>
                <input id="derasarName" name="derasarName" className={styles.formInput} value={form.derasarName} onChange={handleChange} placeholder="Shri Adinath Derasar" />
                {errors.derasarName && <span className={styles.formError}>{errors.derasarName}</span>}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="addressLine1">Address Line 1 *</label>
              <input id="addressLine1" name="addressLine1" className={styles.formInput} value={form.addressLine1} onChange={handleChange} placeholder="Street / Area / Locality" />
              {errors.addressLine1 && <span className={styles.formError}>{errors.addressLine1}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="addressLine2">Address Line 2 (optional)</label>
              <input id="addressLine2" name="addressLine2" className={styles.formInput} value={form.addressLine2} onChange={handleChange} placeholder="Landmark, building, etc." />
            </div>

            <div className={styles.formRow}>
              <StateCitySelect
                state={form.state}
                city={form.city}
                onStateChange={handleStateChange}
                onCityChange={handleCityChange}
                required
                formGroupClassName={styles.formGroup}
                labelClassName={styles.formLabel}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="pincode">Pincode *</label>
              <input id="pincode" name="pincode" className={styles.formInput} value={form.pincode} onChange={handleChange} placeholder="380001" />
              {errors.pincode && <span className={styles.formError}>{errors.pincode}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="message">Anything you'd like to tell us? (optional)</label>
              <textarea id="message" name="message" className={styles.formTextarea} value={form.message} onChange={handleChange} placeholder="Tell us about your Derasar, expected number of members, any specific questions…" style={{ minHeight: 100 }} />
            </div>

            {submitError && <p className={styles.formError}>{submitError}</p>}

            <button type="submit" className={styles.btnSubmit} disabled={submitting}>
              <FiSend size={15} />
              {submitting ? 'Submitting…' : 'Request Demo Access'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default RequestDemoPage;
